import get from 'lodash/get';
import has from 'lodash/has';

import { getAiSuggestion } from '@/api';
import WidgetService from '@/utils/WidgetService';
import * as WorkflowNodeService from '@/utils/workflow-node-service';
import { isArrayOfObjects, flattenPreserveArrays, unflatten } from '@/utils/helpers';
import { GLOBAL_RUN_DATA } from '@/utils/workflow-node.const';

type UUID = string;
type JSONValue = string | number | boolean | JSONObject | JSONArray;

interface JSONObject {
	[x: string]: JSONValue;
}

interface JSONArray extends Array<JSONValue> {}

interface ActionParam {
	key: string;
	name: string;
	description: string;
	type: string;
	required?: boolean;
}

type WorkflowNode = { [key: string]: unknown; uuid: UUID; node_title: string };
type Service = { [key: string]: unknown; service_name: string };

interface StepInfo {
	stepApp: string;
	stepTitle: string;
	description: string;
}

interface FormattedPreviousStep extends StepInfo {
	uuid: UUID;
	output: JSONValue;
	outputLabels: { [key: string]: string };
}

interface AutofillData {
	currentStep: StepInfo & {
		paramToAutofill: ActionParam;
	};
	featureDescription: string;
	previousSteps: FormattedPreviousStep[];
}

interface ServiceAction {
	key: string;
	description: string;
	title: string;
}

type WorkflowVersion = JSONObject & { workflow_title: string };

interface GetDisplayKeyInput extends Pick<Getters, 'getWidget' | 'getAction'> {
	key: string;
	value: JSONValue;
	node: WorkflowNode;
}

interface Getters {
	getNodeByUuidForWorkflowVersion: Function;
	getService: Function;
	getAction: Function;
	getWidget: Function;
}

interface FormatPreviousStepsInput extends Getters {
	previousSteps: JSONObject;
	workflowVersionId: number;
}

interface SuggestAutofillInput {
	workflowVersion: WorkflowVersion;
	service: Service;
	action: ServiceAction;
	param: ActionParam;
	previousSteps: JSONObject;
	getters: Getters;
}

const WIDGET_OUTPUT_PREFIX = 'values.';

const isTable = isArrayOfObjects;

const transformParamForPrompt = ({ key, name, description, type }: ActionParam) => ({ key, name, description, type });

function isRecord(input: unknown): input is Record<string, unknown> {
	return typeof input === 'object' && input !== null && !Array.isArray(input) && typeof input !== 'function';
}

const truncateObject = (d: Record<string, unknown>, maxLength = 100) => {
	for (const k in d) {
		const v = d[k];
		if (isRecord(v)) {
			d[k] = truncateObject(v, maxLength);
		} else if (typeof v === 'string' && v.length > maxLength) {
			d[k] = v.substring(0, maxLength) + '...(truncated)';
		}
	}
	return d;
};

const getDisplayKey = ({ key, value, node, getWidget, getAction }: GetDisplayKeyInput) => {
	let displayKey = key;
	const testResultOutputLabels = WorkflowNodeService.getTestResponseOutputLabels(node);
	if (WorkflowNodeService.isWidget(node) && key.includes(WIDGET_OUTPUT_PREFIX)) {
		const widget = getWidget(WorkflowNodeService.getNodeWidgetId(node));
		if (widget) {
			displayKey = WidgetService.replaceKeyWithIdentifier(widget, key);
		}
	} else if (WorkflowNodeService.isConsolidateDataNode(node, getAction)) {
		displayKey = WorkflowNodeService.getConsolidateDataParamName(node, key);
	} else if (!isTable(value) && testResultOutputLabels && key in testResultOutputLabels) {
		displayKey = WorkflowNodeService.getValueOfTestResponseOutputLabel(testResultOutputLabels[key]);
	}
	return displayKey;
};

const buildTruncatedPreviousSteps = (previousSteps: Record<string, unknown>): JSONObject => {
	const copy = JSON.parse(JSON.stringify(previousSteps));
	return Object.entries(copy).reduce((result: JSONObject, [key, value]) => {
		if (key !== GLOBAL_RUN_DATA && isRecord(value) && isRecord(result)) {
			// @ts-ignore
			result[key] = truncateObject(value);
		}
		return result;
	}, {});
};

const formatPreviousSteps = ({
	previousSteps,
	getNodeByUuidForWorkflowVersion,
	getService,
	getWidget,
	getAction,
	workflowVersionId,
}: FormatPreviousStepsInput): FormattedPreviousStep[] => {
	const truncatedPreviousSteps = buildTruncatedPreviousSteps(previousSteps);
	return Object.entries(truncatedPreviousSteps).map(([prevStepUUID, prevStepTruncatedOutput]): FormattedPreviousStep => {
		const node: WorkflowNode = getNodeByUuidForWorkflowVersion(prevStepUUID, workflowVersionId);
		const service: Service = getService(node.service);
		const action = getAction(node.action) || { description: '' };
		const flatOutput: JSONObject = flattenPreserveArrays(prevStepTruncatedOutput);

		const flatOutputLabels = Object.keys(flatOutput).reduce((result: JSONObject, key) => {
			const displayKey = getDisplayKey({ key, value: flatOutput[key], node, getWidget, getAction });
			if (displayKey && key !== displayKey) {
				result[key] = displayKey;
			}
			return result;
		}, {});

		const outputLabels = unflatten(flatOutputLabels);

		return {
			uuid: node.uuid,
			stepApp: service.service_name,
			stepTitle: node.node_title,
			description: action.description,
			output: prevStepTruncatedOutput,
			outputLabels,
		};
	});
};

const buildAutofillData = ({ workflowVersion, service, action, param, previousSteps, getters }: SuggestAutofillInput): AutofillData => ({
	featureDescription: workflowVersion.workflow_title,
	currentStep: {
		stepApp: service.service_name,
		stepTitle: action.title,
		description: action.description,
		paramToAutofill: transformParamForPrompt(param),
	},
	previousSteps: formatPreviousSteps({
		previousSteps,
		workflowVersionId: Number(workflowVersion.id),
		...getters,
	}),
});

const formatAiKeyToOurNotation = (key: string) => `{{${key.replace('.', '__')}}}`;

export const suggestAutofill = async (input: SuggestAutofillInput) => {
	console.log(`Suggesting autofill for ${input.service.service_name} ${input.action.title}: ${input.param.name} (${input.param.key})`);
	const data = buildAutofillData(input);
	const rawResponse = await getAiSuggestion(data);
	if (typeof rawResponse !== 'string') {
		throw new Error(`response is of unexpected type: ${typeof rawResponse}, ${rawResponse}`);
	}
	const suggestedKey = JSON.parse(rawResponse);
	if (!has(input.previousSteps, suggestedKey)) {
		console.error('previous steps', input.previousSteps);
		throw new Error(`suggested autofill was not found in previous steps: ${suggestedKey}\n${JSON.stringify(input.previousSteps)}`);
	} else {
		const suggestedValue = get(input.previousSteps, suggestedKey);
		console.log(`suggested value: ${suggestedValue} from key ${suggestedKey}`);
	}
	return formatAiKeyToOurNotation(suggestedKey);
};

export const shouldAutofillParam = (param: ActionParam) => Boolean(param.required);
